import { useState, useEffect } from 'react';
import { ChatMessage } from '../types';

const MAX_MESSAGES_PER_DAY = 5;
const STORAGE_KEY = 'chatbot_history';

interface ChatHistory {
  date: string;
  messages: ChatMessage[];
  messageCount: number;
}

export function useChatHistory() {
  const [history, setHistory] = useState<ChatHistory>(() => {
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.date === today) {
        // Convertir les timestamps string en objets Date
        const messagesWithDates = parsed.messages.map((msg: ChatMessage & { timestamp: string }) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        return {
          ...parsed,
          messages: messagesWithDates
        };
      }
    }
    return {
      date: today,
      messages: [],
      messageCount: 0
    };
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  }, [history]);

  const addMessage = (message: ChatMessage) => {
    const today = new Date().toISOString().split('T')[0];
    
    if (today !== history.date) {
      setHistory({
        date: today,
        messages: [message],
        messageCount: 1
      });
      return true;
    }

    if (message.isUser && history.messageCount >= MAX_MESSAGES_PER_DAY) {
      return false;
    }

    setHistory(prev => ({
      ...prev,
      messages: [...prev.messages, message],
      messageCount: message.isUser ? prev.messageCount + 1 : prev.messageCount
    }));
    return true;
  };

  const canSendMessage = () => {
    const today = new Date().toISOString().split('T')[0];
    return today !== history.date || history.messageCount < MAX_MESSAGES_PER_DAY;
  };

  const getMessagesLeft = () => {
    const today = new Date().toISOString().split('T')[0];
    if (today !== history.date) return MAX_MESSAGES_PER_DAY;
    return MAX_MESSAGES_PER_DAY - history.messageCount;
  };

  return {
    messages: history.messages,
    addMessage,
    canSendMessage,
    getMessagesLeft
  };
}
